import { useState, useCallback } from 'react'

type UsePostsWithCommentsToggleResult = {
  showOnlyPostsWithComments: boolean
  togglePostsWithComments: (value: boolean) => void
  showAllPosts: () => void
}

/**
 * Manages the "show only posts with comments" toggle state.
 * Calls `onSelectionReset` on every change so the parent can clear the selected post.
 * Calls `onToggle` with the new value for tracking or side effects.
 */
export function usePostsWithCommentsToggle({
  onSelectionReset,
  onToggle,
}: {
  onSelectionReset: () => void
  onToggle?: (value: boolean) => void
}): UsePostsWithCommentsToggleResult {
  const [showOnlyPostsWithComments, setShowOnlyPostsWithComments] =
    useState(true)

  const togglePostsWithComments = useCallback(
    (value: boolean) => {
      setShowOnlyPostsWithComments(value)
      onSelectionReset()
      onToggle?.(value)
    },
    [onSelectionReset, onToggle],
  )

  const showAllPosts = useCallback(() => {
    togglePostsWithComments(false)
  }, [togglePostsWithComments])

  return { showOnlyPostsWithComments, togglePostsWithComments, showAllPosts }
}
